import api from './api';

export interface Job {
  id?: number;
  employer_id: number;
  company_name?: string;
  website_url?: string;
  title: string;
  job_language?: string;
  work_field?: string;
  description: string;
  short_description?: string;
  location: string;
  position_type?: string;
  job_type: string;
  work_time?: string;
  positions?: string;
  salary_min?: number;
  salary_max?: number;
  salary_currency?: string;
  hide_salary?: boolean;
  requirements?: string;
  responsibilities?: string;
  benefits?: string;
  company_offers?: string;
  guarantee_confidentiality?: boolean;
  inform_selected_only?: boolean;
  additional_info?: string;
  start_date?: string;
  start_date_type?: string;
  application_deadline?: string;
  company_activity?: string;
  contact_email?: string;
  company_description?: string;
  client_id?: number | null;
  hide_client?: boolean;
  folder_id?: number | null;
  posting_duration_weeks?: number | null;
  expires_at?: string | null;
  status: 'draft' | 'pending' | 'published' | 'closed' | 'archived' | 'declined';
  views_count?: number;
  applications_count?: number;
  created_at?: string;
  updated_at?: string;
  published_at?: string;
}

export interface JobWithEmployer extends Job {
  company_name?: string;
  company_logo?: string;
  website_url?: string;
  employer_email?: string;
  employer_company_activity?: string;
  employer_company_description?: string;
  client_name?: string;
  client_activity_sector?: string;
  client_descriptions?: string[];
}

export interface JobCategory {
  id: number;
  name: string;
  slug: string;
  description?: string;
}

export interface CreateJobData {
  title: string;
  company_name?: string; // Įmonės pavadinimas
  website_url?: string; // Įmonės internetinė svetainė
  job_language?: string; // Skelbimo kalba
  work_field?: string; // Darbo sritis
  location: string; // Darbovietės miestas
  position_type?: string; // Pareigybės tipas
  job_type: string; // Darbo tipas
  work_time?: string; // Darbo laikas
  positions?: string; // Pareigybės
  description: string; // Darbo aprašymas
  requirements?: string; // Reikalavimai kandidatams
  salary_min?: number; // Atlyginimas nuo
  salary_max?: number; // Atlyginimas iki
  salary_currency?: string;
  hide_salary?: boolean; // Nerodyti atlyginimo skelbime
  benefits?: string; // Privalumai
  company_offers?: string; // Įmonė siūlo
  guarantee_confidentiality?: boolean; // Konfidencialumą garantuojame
  inform_selected_only?: boolean; // Informuosime tik atrinktus kandidatus
  additional_info?: string; // Papildoma informacija apie darbą
  start_date?: string; // Darbo pradžia (date)
  start_date_type?: string; // Darbo pradžia (type: Nedelsiant, Per savaitę)
  short_description?: string;
  responsibilities?: string;
  application_deadline?: string; // Prašymus siųsti iki
  company_activity?: string; // Įmonės veiklos sritis
  contact_email?: string; // El.paštas
  company_description?: string; // Trumpas įmonės veiklos aprašymas
  client_id?: number | null;
  hide_client?: boolean;
  hide_company?: boolean; // Nerodyti įmonės duomenų
  posting_duration_weeks?: number | null;
  expires_at?: string | null;
  status?: 'draft' | 'pending' | 'published';
}

export interface JobResponse {
  success: boolean;
  message?: string;
  data?: {
    job: Job;
  };
}

export interface JobsResponse {
  success: boolean;
  message?: string;
  data?: {
    jobs: JobWithEmployer[];
  };
}

export interface CategoriesResponse {
  success: boolean;
  message?: string;
  data?: {
    categories: JobCategory[];
  };
}

class JobService {
  // Create a new job
  async createJob(jobData: CreateJobData): Promise<JobResponse> {
    try {
      console.log('📤 JobService.createJob - Sending data:', jobData);
      const response = await api.post('/jobs', jobData);
      console.log('📥 JobService.createJob - Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ JobService.createJob - Error:', error);
      console.error('Error response:', error.response?.data);
      return {
        success: false,
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to create job'
      };
    }
  }

  // Get current user's jobs (employer only)
  async getMyJobs(): Promise<JobsResponse> {
    try {
      const response = await api.get('/jobs/my/jobs');
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch jobs'
      };
    }
  }

  // Get employer jobs (for dropdowns, returns simple array)
  async getEmployerJobs(): Promise<Array<{ id: number; title: string }>> {
    try {
      const response = await api.get('/jobs/my/jobs');
      console.log('Get employer jobs response:', response.data);
      
      // Handle different response formats
      let jobs: Job[] = [];
      if (response.data.success && response.data.data) {
        // Response format: { success: true, data: { jobs: [...] } }
        if (Array.isArray(response.data.data.jobs)) {
          jobs = response.data.data.jobs;
        } 
        // Response format: { success: true, data: [...] }
        else if (Array.isArray(response.data.data)) {
          jobs = response.data.data;
        }
      } else if (Array.isArray(response.data)) {
        jobs = response.data;
      } else {
        console.warn('Unexpected response format:', response.data);
        return [];
      }
      
      return jobs
        .filter(job => job.id && job.title)
        .map((job: Job) => ({
          id: job.id!,
          title: job.title
        }));
    } catch (error: any) {
      console.error('Failed to fetch employer jobs:', error);
      return [];
    }
  }

  // Get all published jobs (public) with optional search filters
  async getPublishedJobs(
    limit?: number, 
    offset?: number,
    filters?: {
      keywords?: string;
      location?: string;
      job_type?: string;
      work_field?: string;
      employer_id?: string;
    }
  ): Promise<JobsResponse> {
    try {
      const params = new URLSearchParams();
      if (limit) params.append('limit', limit.toString());
      if (offset) params.append('offset', offset.toString());
      
      // Pass search filters to backend for flexible search
      if (filters?.keywords) params.append('keywords', filters.keywords);
      if (filters?.location) params.append('location', filters.location);
      if (filters?.job_type) params.append('job_type', filters.job_type);
      if (filters?.work_field) params.append('work_field', filters.work_field);
      if (filters?.employer_id) params.append('employer_id', filters.employer_id);
      
      const response = await api.get(`/jobs/published?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch jobs'
      };
    }
  }

  // Get job by ID
  async getJobById(id: number): Promise<{ success: boolean; message?: string; data?: { job: JobWithEmployer } }> {
    try {
      const response = await api.get(`/jobs/${id}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch job'
      };
    }
  }

  // Get similar jobs
  async getSimilarJobs(id: number, limit: number = 4): Promise<{ success: boolean; data?: { jobs: JobWithEmployer[] } }> {
    try {
      const response = await api.get(`/jobs/${id}/similar?limit=${limit}`);
      return response.data;
    } catch (error: any) {
      return { success: false };
    }
  }

  // Update job
  async updateJob(id: number, jobData: Partial<CreateJobData>): Promise<JobResponse> {
    try {
      console.log(`📤 JobService.updateJob - Updating job ${id} with data:`, jobData);
      const response = await api.put(`/jobs/${id}`, jobData);
      console.log('📥 JobService.updateJob - Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ JobService.updateJob - Error:', error);
      console.error('Error response:', error.response?.data);
      return {
        success: false,
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to update job'
      };
    }
  }

  // Publish job (submit for approval)
  async publishJob(id: number): Promise<JobResponse> {
    try {
      const response = await api.patch(`/jobs/${id}/publish`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to publish job'
      };
    }
  }

  // Unpublish job (set back to draft, no approval needed)
  async unpublishJob(id: number): Promise<JobResponse> {
    try {
      const response = await api.patch(`/jobs/${id}/unpublish`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to unpublish job'
      };
    }
  }

  // Delete job
  async deleteJob(id: number): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await api.delete(`/jobs/${id}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to delete job'
      };
    }
  }

  // Duplicate a job — backend creates a draft copy with the same content.
  async duplicateJob(id: number): Promise<JobResponse> {
    try {
      const response = await api.post(`/jobs/${id}/duplicate`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to duplicate job'
      };
    }
  }

  /**
   * Download a job posting as a PDF. Triggers a browser download by
   * fetching the binary stream and synthesising an <a download> click.
   * Resolves to true on success.
   */
  async downloadJobPdf(id: number, fallbackTitle?: string): Promise<boolean> {
    try {
      const response = await api.get(`/jobs/${id}/pdf`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const cd = response.headers?.['content-disposition'] as string | undefined;
      const match = cd ? /filename="?([^"]+)"?/i.exec(cd) : null;
      const filename =
        match?.[1] ||
        `${(fallbackTitle || 'darbo-skelbimas')
          .replace(/[^a-zA-Z0-9]+/g, '-')
          .toLowerCase()}-${id}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
      return true;
    } catch (error: any) {
      console.error('downloadJobPdf failed:', error);
      return false;
    }
  }

  // Search jobs
  async searchJobs(params: {
    query?: string;
    location?: string;
    job_type?: string;
    salary_min?: number;
    category?: string;
    limit?: number;
    offset?: number;
  }): Promise<JobsResponse> {
    try {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          searchParams.append(key, value.toString());
        }
      });
      
      const response = await api.get(`/jobs/search?${searchParams.toString()}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to search jobs'
      };
    }
  }

  // Get job categories
  async getCategories(): Promise<CategoriesResponse> {
    try {
      const response = await api.get('/jobs/categories');
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch categories'
      };
    }
  }

  // Get all jobs (admin only)
  async getAllJobs(limit?: number, offset?: number, status?: string): Promise<JobsResponse> {
    try {
      const params = new URLSearchParams();
      if (limit) params.append('limit', limit.toString());
      if (offset) params.append('offset', offset.toString());
      if (status) params.append('status', status);
      
      const response = await api.get(`/jobs/admin/all?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch jobs'
      };
    }
  }

  // Helper method to format job type for display
  formatJobType(jobType: string): string {
    const jobTypeMap: { [key: string]: string } = {
      'full_time': 'Pastovus',
      'part_time': 'Dalinis etatas',
      'contract': 'Kontraktinis',
      'internship': 'Praktika',
      'temporary': 'Laikinas'
    };
    return jobTypeMap[jobType] || jobType;
  }

  // Helper method to format status for display
  formatStatus(status: string): string {
    const statusMap: { [key: string]: string } = {
      'draft': 'Juodraštis',
      'published': 'Publikuotas',
      'closed': 'Uždarytas',
      'archived': 'Archyvuotas'
    };
    return statusMap[status] || status;
  }

  // Helper method to format salary range
  formatSalary(salaryMin?: number, salaryMax?: number, currency: string = 'EUR'): string {
    if (!salaryMin && !salaryMax) {
      return 'Derinama';
    }
    
    const currencySymbol = currency === 'EUR' ? '€' : currency;
    
    if (salaryMin && salaryMax) {
      return `${salaryMin} - ${salaryMax} ${currencySymbol}`;
    } else if (salaryMin) {
      return `nuo ${salaryMin} ${currencySymbol}`;
    } else if (salaryMax) {
      return `iki ${salaryMax} ${currencySymbol}`;
    }
    
    return 'Derinama';
  }
}

const jobService = new JobService();
export default jobService;
